import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AddBankAccountInput } from '@/lib/payments/paymentAccount.types';
import { requestCreateBankProfile, requestVerifyAch } from './paymentApi';
import { memberBillingProfileKey } from './useMemberBillingProfile';

interface UseAddBankAccountOptions {
  onSuccess?: () => void;
}

/**
 * Submits a new bank account via the two-step gateway flow: verify ACH (account/routing → token)
 * then create the profile (token + last4 only). The full account number never reaches create.
 */
export function useAddBankAccount({ onSuccess }: UseAddBankAccountOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: AddBankAccountInput) => {
      const verified = await requestVerifyAch({
        accountNumber: input.accountNumber,
        routingNumber: input.routingNumber,
      });

      return requestCreateBankProfile({
        achInfo: {
          last4: input.accountNumber.slice(-4),
          bankName: input.bankName,
          accountName: input.billing.name,
          routingNumber: input.routingNumber,
          accountType: input.accountType,
          hsa: input.isHsa,
        },
        gatewayTokenId: verified.gatewayTokenId,
        authTransactionId: verified.authTransactionId,
        isDefault: input.makeDefault,
        autoPay: input.autoPay,
        billing: input.billing,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberBillingProfileKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const code = extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'addBankAccount', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return { submit: (input: AddBankAccountInput) => mutation.mutate(input), isPending: mutation.isPending };
}
